/**
 * Phase 2H-Fix2 — Month grid badges & multi-day utsava spans (sunrise-bound API data).
 */

/** Tithi numbers at sunrise → badge (first civil day of each consecutive run). */
export const TITHI_BADGE_DEFS = [
  { key: "purnima", label: "Pūrṇimā", numbers: [15] },
  { key: "amavasya", label: "Amāvasyā", numbers: [30] },
  { key: "ekadashi", label: "Ekādaśī", numbers: [11, 26] },
  { key: "ashtami", label: "Aṣṭamī", numbers: [8, 23] },
  { key: "navami", label: "Navamī", numbers: [9, 24] },
  { key: "trayodashi", label: "Trayodaśī", numbers: [13, 28] },
  { key: "chaturdashi", label: "Chaturdaśī", numbers: [14, 29] },
];

/** First civil day of a consecutive sunrise tithi run (handles midnight span). */
export function isFirstDayOfTithiRun(days, index, tithiNumber) {
  if (!days[index] || days[index].tithi?.number !== tithiNumber) return false;
  const prev = index > 0 ? days[index - 1]?.tithi?.number : null;
  return prev !== tithiNumber;
}

/**
 * Per-day grid badges from hybrid month `days[]` (already sunrise-bound).
 */
export function computeDayGridBadges(days) {
  if (!days?.length) return [];
  return days.map((day, i) => {
    const tithiNum = day.tithi?.number;
    const tithiBadges = [];
    for (const def of TITHI_BADGE_DEFS) {
      if (def.numbers.includes(tithiNum) && isFirstDayOfTithiRun(days, i, tithiNum)) {
        tithiBadges.push({ key: def.key, label: def.label });
      }
    }
    const festivals = [];
    if (day.festivals?.length) {
      for (const f of day.festivals) {
        const prevList = i > 0 ? days[i - 1].festivals || [] : [];
        if (!prevList.some((p) => p.id === f.id)) {
          festivals.push(f);
        }
      }
    }
    return { tithiBadges, festivals };
  });
}

function formatRangeLabel(startDay, endDay, monthName) {
  const s = startDay.gregorian?.day;
  const e = endDay.gregorian?.day;
  if (s == null || e == null) return monthName;
  if (s === e) return `${monthName} ${s}`;
  return `${monthName} ${s}–${e}`;
}

function closeSpan(days, monthName, start, end, meta, spans, minDays) {
  if (start == null || end < start) return;
  const len = end - start + 1;
  if (len < minDays) return;
  const sourceDay = days[start];
  spans.push({
    id: meta.id,
    name: meta.name,
    dateRange: formatRangeLabel(days[start], days[end], monthName),
    startIndex: start,
    endIndex: end,
    sourceDay,
  });
}

function scanRuns(days, monthName, predicate, metaFn, minDays = 2) {
  const spans = [];
  let start = null;
  for (let i = 0; i <= days.length; i++) {
    const active = i < days.length && predicate(days[i]);
    if (active && start === null) start = i;
    if (!active && start !== null) {
      closeSpan(days, monthName, start, i - 1, metaFn(days[start], days[i - 1]), spans, minDays);
      start = null;
    }
  }
  return spans;
}

/**
 * Multi-day utsavas for collapsible section below the grid.
 */
export function buildUtsavaSpans(days, monthName) {
  if (!days?.length) return [];

  const spans = [];

  scanRuns(
    days,
    monthName,
    (d) => d.cultural_layer?.is_adhika_masa,
    () => ({ id: "adhika_masa", name: "Adhika Masa Utsava" }),
    2
  ).forEach((s) => spans.push(s));

  const festivalIds = [];
  days.forEach((d) => {
    d.festivals?.forEach((f) => {
      if (!festivalIds.includes(f.id)) festivalIds.push(f.id);
    });
  });

  for (const fid of festivalIds) {
    scanRuns(
      days,
      monthName,
      (d) => d.festivals?.some((f) => f.id === fid),
      (startDay) => ({
        id: `festival_${fid}`,
        name: startDay.festivals.find((f) => f.id === fid)?.name_en || fid,
      }),
      2
    ).forEach((s) => spans.push(s));
  }

  scanRuns(
    days,
    monthName,
    (d) => d.is_purnima,
    () => ({ id: "purnima_span", name: `${monthName} Pūrṇimā Utsava` }),
    2
  ).forEach((s) => spans.push(s));

  return spans.sort((a, b) => a.startIndex - b.startIndex);
}

export function firstWeekdayPadding(year, month) {
  return new Date(Date.UTC(year, month - 1, 1, 12, 0, 0)).getUTCDay();
}

export function padMonthDays(days, year, month) {
  const pad = firstWeekdayPadding(year, month);
  return [...Array(pad).fill(null), ...(days || [])];
}
